import {
  BaseEventBuilder,
  CreditPlanTier,
} from '@droidmap/shared-service-contract'

import { OrganisationInvitation } from '../models/organisation-invitation.js'
import {
  FrozenReason,
  Group,
  GroupUser,
  Organisation,
  OrganisationUser,
} from '../models/organisation-models.js'

const serviceSource = 'droidmap.backend.organisation-service'

/* ------------------------------ Organisation ------------------------------ */

export const OrganisationCreated = new BaseEventBuilder<
  Organisation,
  'Organisation Created'
>(serviceSource, 'Organisation Created')

export const OrganisationUpdated = new BaseEventBuilder<
  Organisation,
  'Organisation Updated'
>(serviceSource, 'Organisation Updated')

/**
 * The destructive end of an org's life: emitted when the org row is gone and
 * every consumer should hard-delete what it holds (assets, projects, the
 * datalake row; org-service itself ends the Lago subscription). Since #2973 a
 * real org reaches this only after {@link OrganisationDeletionScheduled} plus
 * the 30-day `ORG_DELETION_RETENTION_DAYS` window, when the sweeper purges
 * it; test orgs and memberless orphans still get it immediately.
 */
export const OrganisationDeleted = new BaseEventBuilder<
  Organisation,
  'Organisation Deleted'
>(serviceSource, 'Organisation Deleted')

/**
 * An org entered `pending_delete` (#2973): an admin deleted it, or the sweeper
 * moved a frozen org past its grace window. Nothing is destroyed yet — the org
 * is inaccessible but restorable until `orgPurgeDueAt`. org-service consumes
 * its own event to end the Lago subscription straight away, so a deleted org
 * is not invoiced through the retention window.
 */
export const OrganisationDeletionScheduled = new BaseEventBuilder<
  Organisation,
  'Organisation Deletion Scheduled'
>(serviceSource, 'Organisation Deletion Scheduled')

/**
 * A `pending_delete` org was restored inside its retention window (#2973).
 * The payload is the restored org: `trial` (org-service re-provisions its
 * Starter subscription) or `frozen` awaiting a Pro upgrade.
 */
export const OrganisationRestored = new BaseEventBuilder<
  Organisation,
  'Organisation Restored'
>(serviceSource, 'Organisation Restored')

/* ------------------------------- Org Budget ------------------------------- */

/**
 * Which customer-budget threshold a {@link OrgBudgetThresholdCrossed} event
 * reports (#1914, ADR-0011 D5):
 *
 *   - `budget_warning` — month-to-date spend crossed 80% of the budget.
 *                        Always notify-only.
 *   - `budget_reached` — spend reached 100% of the budget. Notify; the quota
 *                        enforcer also *blocks* iff `budget_enforce` is set.
 */
export type OrgBudgetThreshold = 'budget_warning' | 'budget_reached'

/**
 * Emitted by the organisation-service when its platform-side budget detector
 * (the #1865 reconciler) observes month-to-date spend newly cross one of the
 * customer's budget thresholds (#1914, ADR-0011 D5). "Newly" = the org row's
 * crossing-state column was `null` and is now set, so redelivery / a steady
 * over-budget org does NOT re-emit.
 *
 * Unlike the inbound `OrgBillingStateChanged` family (which flows analytics →
 * backend), this is emitted *by* the backend org-service for a downstream
 * notification consumer (notification-service, a follow-up track) — detection
 * is platform-side because Lago's native budget alerts are Premium-only (D5
 * open item #1). The enforcement decision does NOT depend on this event: the
 * quota enforcer gates on spend-vs-budget directly. This event exists purely
 * to drive the customer-facing warn / reached notification.
 */
export interface OrgBudgetThresholdCrossedDetail {
  /** The org whose budget threshold was crossed. */
  orgId: string

  /**
   * The org's name, so an email can say which org it means: someone can be an
   * admin of several. Absent on an event from before this field.
   */
  orgName?: string

  /** Which threshold crossed — see {@link OrgBudgetThreshold}. */
  threshold: OrgBudgetThreshold

  /** The customer-set budget in cents that was crossed. */
  monthlyBudgetCents: number

  /** Month-to-date spend in cents at the time of the crossing. */
  monthlySpendCents: number

  /**
   * Whether the budget is in enforce mode. `true` ⇒ a `budget_reached`
   * crossing is also blocking cost-incurring routes (the notification should
   * say so); `false` ⇒ notify-only. Carried so the notification consumer can
   * word the message without re-reading the org row.
   */
  budgetEnforce: boolean

  /**
   * The org is on an ADR-0027 credit plan, where the budget is a cap on overage
   * and the spend is overage alone (#3002). The email has to say so, and has to
   * say what the cap doesn't stop (D5). Absent on an event from before this field.
   */
  isCreditPlan?: boolean
}

export const OrgBudgetThresholdCrossed = new BaseEventBuilder<
  OrgBudgetThresholdCrossedDetail,
  'Org Budget Threshold Crossed'
>(serviceSource, 'Org Budget Threshold Crossed')

/* --------------------------- Credit bundle used --------------------------- */

/**
 * Which point in a credit plan's included bundle a
 * {@link OrgCreditBundleThresholdCrossed} event reports (#3002, ADR-0027 D5):
 *
 *   - `bundle_warning` — credits drawn this period crossed 80% of the bundle.
 *   - `bundle_reached` — the whole bundle is drawn. On a plan that stops at
 *     the bundle (Trial, Free, or no card) new jobs and uploads now stop; on a
 *     paying plan usage carries on and bills as overage.
 */
export type OrgCreditBundleThreshold = 'bundle_warning' | 'bundle_reached'

/**
 * Emitted by the organisation-service when a credit-plan org's draw-down newly
 * crosses 80% or 100% of its plan's included credits (#3002). "Newly" = the
 * org row's sticky `credits_warning_at` / `credits_reached_at` was `null` and
 * is now set; it re-arms when a new period's draw reads below the threshold.
 *
 * Emitted from both writers of the draw-down: the `wallet.balance_updated`
 * consumer (≤3-minute aggregator cadence) and the nightly reconciler. Like the
 * budget event, enforcement never depends on it — the quota
 * gate reads the draw-down directly. Primitive-only detail, as theirs.
 */
export interface OrgCreditBundleThresholdCrossedDetail {
  orgId: string

  /**
   * The org's name, so an email can say which org it means: someone can be an
   * admin of several. Absent on an event from before this field.
   */
  orgName?: string

  threshold: OrgCreditBundleThreshold

  /** The org's credit plan at the crossing. */
  creditPlanTier: CreditPlanTier

  /** The plan's included credits (decimal string). */
  includedCredits: string

  /** Credits drawn this period at the crossing (decimal string). */
  creditsConsumed: string

  /**
   * Whether usage stops at the bundle for this org (`hard_stop`, or an
   * overage plan with no card) or carries on as overage — the notification's
   * wording turns on it.
   */
  stopsAtBundle: boolean

  /** ISO timestamp of the sticky crossing. */
  crossedAt: string

  /**
   * End of the billing period the crossing happened in (ISO), or `null` when
   * Lago reported none. The notification idempotency discriminator
   * (`<orgId>:<threshold>:<periodEndsAt>`), so the consumer and the nightly
   * reconciler detecting the same crossing send one email per period.
   */
  periodEndsAt: string | null
}

export const OrgCreditBundleThresholdCrossed = new BaseEventBuilder<
  OrgCreditBundleThresholdCrossedDetail,
  'Org Credit Bundle Threshold Crossed'
>(serviceSource, 'Org Credit Bundle Threshold Crossed')

/* ------------------------- Trial-lifecycle emails ------------------------- */

/**
 * Which point in the Starter trial lifecycle a {@link TrialLifecycleNotification}
 * reports (#2074, ADR-0013). Each kind maps to one customer-facing email and is
 * the idempotency discriminator: the email provider keys its sent-marker on
 * `<orgId>:<kind>`, so the daily sweeper can re-emit a kind every day it remains
 * in-window and the customer is still mailed exactly once.
 *
 *   - `trial_started`        — org created on the Starter plan (welcome).
 *   - `trial_expiring`       — trial ends within the warning window (~5 days).
 *   - `frozen`               — trial expired; access paused, deletion countdown begins.
 *   - `deletion_warning_30d` — frozen org ~30 days from deletion.
 *   - `deletion_warning_5d`  — frozen org ~5 days from deletion.
 *   - `deletion_warning_1d`  — frozen org ~1 day from deletion (final notice).
 */
export const TRIAL_LIFECYCLE_NOTIFICATION_KINDS = [
  'trial_started',
  'trial_expiring',
  'frozen',
  'deletion_warning_30d',
  'deletion_warning_5d',
  'deletion_warning_1d',
] as const

export type TrialLifecycleNotificationKind =
  (typeof TRIAL_LIFECYCLE_NOTIFICATION_KINDS)[number]

/**
 * Emitted by the organisation-service to drive a single customer-facing
 * trial-lifecycle email (#2074). `trial_started` is emitted from
 * `createOrganisation`; the rest are emitted by the daily
 * `starter-lifecycle-sweeper` as orgs cross each window. The notification-service
 * consumer resolves recipients (org admins) and sends, deduped per
 * `<orgId>:<kind>`.
 *
 * The detail is deliberately primitive-only (no Date fields) so the consumer's
 * Zod schema stays simple and EventBridge JSON round-trips cleanly:
 * `daysRemaining` carries the only number the templates need, with a
 * kind-specific meaning documented on the field.
 */
export interface TrialLifecycleNotificationDetail {
  /** The org the email is about. */
  orgId: string

  /** Org display name, for the subject/body (saves the consumer a read). */
  orgName: string

  /** Which lifecycle email to send — see {@link TrialLifecycleNotificationKind}. */
  kind: TrialLifecycleNotificationKind

  /**
   * Whole days until the relevant deadline, for the message copy:
   *   - `trial_started`        — total trial length (days), or null.
   *   - `trial_expiring`       — days until the trial expires.
   *   - `frozen`               — days in the grace window before deletion.
   *   - `deletion_warning_*`   — days until deletion.
   */
  daysRemaining: number | null

  /**
   * Why the org is frozen, for the `deletion_warning_*` copy (#2598): a
   * `trial_expired` org is told its trial ended; an `awaiting_activation` org
   * never had one and is told it hasn't been activated. Optional — absent on
   * events published before #2598 and on the kinds that only ever concern a
   * trial (`trial_*`, `frozen`); the consumer falls back to the trial copy.
   */
  frozenReason?: FrozenReason

  /**
   * The org is on the credit Trial (ADR-0027 D7, #3004), not the ADR-0013 Starter
   * trial, so the copy offers credits and says the data stays available. Absent
   * means Starter, which is every event published before #3004.
   */
  creditTrial?: boolean
}

export const TrialLifecycleNotification = new BaseEventBuilder<
  TrialLifecycleNotificationDetail,
  'Trial Lifecycle Notification'
>(serviceSource, 'Trial Lifecycle Notification')

/* ---------------------------- OrganisationUser ---------------------------- */

export const OrganisationUserCreated = new BaseEventBuilder<
  OrganisationUser,
  'Organisation User Created'
>(serviceSource, 'Organisation User Created')

export const OrganisationUserUpdated = new BaseEventBuilder<
  OrganisationUser,
  'Organisation User Updated'
>(serviceSource, 'Organisation User Updated')

export const OrganisationUserDeleted = new BaseEventBuilder<
  OrganisationUser,
  'Organisation User Deleted'
>(serviceSource, 'Organisation User Deleted')

/* ------------------------- OrganisationInvitation ------------------------- */

/**
 * Detail of {@link OrganisationInvitationCreated} (#3056): everything the
 * invitation email needs, so the notifier makes no calls back into
 * org-service. Emitted on create *and* on resend — each one is a send.
 *
 * The invitee's email rides the bus, as it already does on
 * `CognitoPostConfirmation`. It must stay off metric attributes.
 */
export interface OrganisationInvitationCreatedDetail {
  invitationId: string
  orgId: string
  /** The org name as stored — untrusted, user-chosen text. Escape it. */
  orgName: string
  /** The inviter's first name, or `null` when it could not be resolved. */
  inviterFirstName: string | null
  /** The invited address, trimmed and lowercased. */
  email: string
  /** Picks the email variant: review the invite, or create an account. */
  recipientHasAccount: boolean
  /** ISO-8601 instant the invitation expires. */
  expiresAt: string
}

export const OrganisationInvitationCreated = new BaseEventBuilder<
  OrganisationInvitationCreatedDetail,
  'Organisation Invitation Created'
>(serviceSource, 'Organisation Invitation Created')

export const OrganisationInvitationAccepted = new BaseEventBuilder<
  OrganisationInvitation,
  'Organisation Invitation Accepted'
>(serviceSource, 'Organisation Invitation Accepted')

export const OrganisationInvitationDeclined = new BaseEventBuilder<
  OrganisationInvitation,
  'Organisation Invitation Declined'
>(serviceSource, 'Organisation Invitation Declined')

export const OrganisationInvitationRevoked = new BaseEventBuilder<
  OrganisationInvitation,
  'Organisation Invitation Revoked'
>(serviceSource, 'Organisation Invitation Revoked')

/* ---------------------------------- Group --------------------------------- */

export const GroupCreated = new BaseEventBuilder<Group, 'Group Created'>(
  serviceSource,
  'Group Created',
)

export const GroupUpdated = new BaseEventBuilder<Group, 'Group Updated'>(
  serviceSource,
  'Group Updated',
)

export const GroupDeleted = new BaseEventBuilder<Group, 'Group Deleted'>(
  serviceSource,
  'Group Deleted',
)

/* ------------------------------ GroupUser ------------------------------ */

export const GroupUserCreated = new BaseEventBuilder<
  GroupUser,
  'Group User Created'
>(serviceSource, 'Group User Created')

export const GroupUserDeleted = new BaseEventBuilder<
  GroupUser,
  'Group User Deleted'
>(serviceSource, 'Group User Deleted')
